import React, { useCallback, useState } from 'react';
import { Modal, Button, Input, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { InsuranceSetting, InsuranceSurveyPayload } from 'types/common';
import StateInsuranceCard from 'pages/NewUniProfile/components/InsuranceStep/components/StateInsuranceCard';

interface InsuranceSurveyModalProps {
    visible: boolean;
    onSkip: () => void;
    onSave: (payload: InsuranceSurveyPayload) => void;
    states: string[];
    initialBillingNPI?: string;
    initialInsuranceSettings?: Record<string, InsuranceSetting>;
}

const NPI_TOOLTIP = 'Your 10-digit organizational NPI used for billing. This is not your individual NPI.';

const InsuranceSurveyModal: React.FC<InsuranceSurveyModalProps> = ({
    visible,
    onSkip,
    onSave,
    states,
    initialBillingNPI = '',
    initialInsuranceSettings,
}) => {
    const [billingNPI, setBillingNPI] = useState(initialBillingNPI);
    const [openCardState, setOpenCardState] = useState<string | null>(states[0] || null);
    const [insuranceSettings, setInsuranceSettings] = useState<Record<string, InsuranceSetting>>(() => {
        const settings: Record<string, InsuranceSetting> = {};
        states.forEach((state) => {
            settings[state] = initialInsuranceSettings?.[state] || {
                state,
                carriers: [],
                otherCarriers: '',
            };
        });
        return settings;
    });

    const toggleCardState = useCallback((state: string) => {
        setOpenCardState((prev) => (prev === state ? null : state));
    }, []);

    const handleInsuranceChange = (insurance: InsuranceSetting) => {
        setInsuranceSettings((prev) => ({ ...prev, [insurance.state]: insurance }));
    };

    const handleSave = () => {
        onSave({ billingNPI, insuranceSettings });
    };

    return (
        <Modal
            open={visible}
            onCancel={onSkip}
            width={600}
            maskClosable={false}
            zIndex={900}
            className={`${s.modal} ${commonS.modalFixHeightWrap}`}
            footer={[
                <Button key="skip" onClick={onSkip}>
                    Skip
                </Button>,
                <Button key="save" type="primary" onClick={handleSave}>
                    Save
                </Button>,
            ]}
            destroyOnClose
        >
            <div className={s.header}>
                <div className={s.title}>You&apos;re invited to a quick insurance survey</div>
                <div className={s.subtitle}>
                    We&apos;re exploring a low-cost insurance support pilot for providers. This is a survey. You&apos;re
                    not opting in to anything and there&apos;s no commitment. Your responses help us plan. You can also
                    add these later: billing NPI in your profile tab, and payer IDs in your service template.
                </div>
            </div>

            <div className={s.field}>
                <div className={s.fieldLabel}>
                    Billing NPI
                    <Tooltip title={NPI_TOOLTIP} placement="top">
                        <QuestionCircleOutlined className={s.helpIcon} />
                    </Tooltip>
                </div>
                <Input
                    placeholder="Enter your billing NPI number"
                    value={billingNPI}
                    onChange={(e) => setBillingNPI(e.target.value)}
                />
            </div>

            <div className={s.divider} />

            <div className={s.sectionTitle}>Payer IDs</div>
            <div className={s.sectionDesc}>
                Add the Payer ID for each insurance carrier you accept. You can find this on your existing claims or
                EOBs.
            </div>

            <div className={s.statesList}>
                {states.map((state) => (
                    <StateInsuranceCard
                        key={state}
                        state={state}
                        insurance={insuranceSettings[state]}
                        onChange={handleInsuranceChange}
                        onToggle={() => toggleCardState(state)}
                        isOpen={openCardState === state}
                        showPayerId
                    />
                ))}
            </div>
        </Modal>
    );
};

export default InsuranceSurveyModal;
